import { concatHex, keccak256, recoverMessageAddress, toBytes } from "viem";
import type { Address, Hex } from "viem";
import { canonicalJson } from "./json";
import type { MessageSigner } from "./signing";

/** One receipt per billable unit, signed by the service's submitter (CONTRACT.md 0.4). */
export interface Receipt {
  service: string;
  client: Address;
  identity: Address;
  token: Address;
  call: string;
  unit: string;
  measures: Record<string, number | string>;
  actual: string;
  pricing: Hex;
  signature?: Hex;
}

export type UnsignedReceipt = Omit<Receipt, "signature">;

function withoutSignature(r: Receipt | UnsignedReceipt): UnsignedReceipt {
  const { signature: _s, ...rest } = r as Receipt;
  void _s;
  return rest;
}

export function receiptHash(r: Receipt | UnsignedReceipt): Hex {
  return keccak256(toBytes(canonicalJson(withoutSignature(r))));
}

/** The receipt's signature is personal_sign over its hash as a hex string message. */
export async function signReceipt(r: UnsignedReceipt, submitter: MessageSigner): Promise<Receipt> {
  const signature = await submitter.signMessage({ message: receiptHash(r) });
  return { ...r, signature };
}

export async function receiptSigner(r: Receipt): Promise<Address | null> {
  if (!r.signature) return null;
  try {
    return await recoverMessageAddress({ message: receiptHash(r), signature: r.signature });
  } catch {
    return null;
  }
}

/** context = keccak256(concat(receiptHash_i)) in batch order. */
export function batchContext(receipts: Array<Receipt | UnsignedReceipt>): Hex {
  if (receipts.length === 0) return keccak256("0x");
  return keccak256(concatHex(receipts.map(receiptHash)));
}

export function batchSum(receipts: Array<Receipt | UnsignedReceipt>): bigint {
  return receipts.reduce((sum, r) => sum + BigInt(r.actual), 0n);
}
